import { fieldValidationHelper, addressFieldValidationHelper, validateEmailWithBackend } from "@/form/helpers/field-validations.js";
import { TRIGGERS, EMAIL_UNDELIVERABLE_MESSAGE, visibleErrors } from "@/form/helpers/validation-display.js";

/**
 * Extracted validation logic from LcForm.
 * All state is accessed via refs from the component.
 *
 * @param {Object} deps
 * @param {import('vue').Ref} deps.app - ref
 * @param {import('vue').Ref} deps.config - ref
 * @param {Object} deps.applicationSettings - reactive (writable computed)
 * @param {Function} deps.updateApplication
 * @param {import('vue').Ref} deps.activeStep - ref
 * @param {import('vue').Ref} deps.validator - ref
 * @param {import('vue').Ref} deps.hiddenFields - ref
 * @param {import('vue').Ref} deps.hiddenFieldsIfCondition - ref
 * @param {import('vue').Ref} deps.editActiveStep - ref
 * @param {Object} deps.loading - useLoadingAnimation return object
 * @param {import('vue').Ref} deps.shouldAutoSkipStep - ref set to true when a step should be auto-skipped
 */
export function useFormValidation(deps) {

    function isHiddenField(fieldObject) {
        return hideField(fieldObject);
    }

    /**
     * Checks whether a pre-filled value is valid for a given field by running
     * it through the field's validation rules and verifying it exists in the
     * field's choices list (for radio/select fields).
     *
     * @param {Object} fieldObject - The field definition from the step config.
     * @returns {boolean}
     */
    function isPrefilledValueValid(fieldObject) {
        const app = deps.app.value;
        const country = deps.config.value.country;
        const value = app[fieldObject.name];

        // Rule validation
        const validatorEntry = deps.validator.value[fieldObject.name];
        const rules = validatorEntry?.rules || fieldObject.validationRules;

        if (rules) {
            // Address fields are never pre-filled as an object — individual
            // address components (city, province, postalCode) arrive as flat
            // GET parameters and are assembled into the address object later,
            // so addressFieldValidationHelper would always fail here.
            if (fieldObject.name === 'address') {
                // if (!addressFieldValidationHelper(value, country).isValid) {
                //     return false;
                // }
                return false;
            } else {
                if (!fieldValidationHelper({ value, rules }, app, country).isValid) {
                    return false;
                }
            }
        }

        // Choices validation (radio/select fields)
        // Skip choices check when all option values are numeric — the prefilled
        // value is a specific amount (e.g. 321) that won't match a range bucket
        // (750, 3500, …) but is still valid.
        if (fieldObject.choices) {
            const choicesList = typeof fieldObject.choices === 'function'
                ? fieldObject.choices(app)
                : fieldObject.choices;

            if (Array.isArray(choicesList)) {
                const allNumeric = choicesList.every(c => !isNaN(c.value));

                if (!allNumeric && !choicesList.some(c => c.value == value)) {
                    return false;
                }
            }
        }

        return true;
    }

    function hideField(fieldObject, onInitStep = false) {
        let hide = false;
        let hiddenFields = deps.hiddenFields.value;
        let hiddenFieldsIfCondition = deps.hiddenFieldsIfCondition.value;
        let app = deps.app.value;

        if (onInitStep) {
            if (fieldObject.hasOwnProperty('hideIfHasValue') && !!fieldObject.hideIfHasValue &&
                app.hasOwnProperty(fieldObject.name) && !!app[fieldObject.name]) {
                hide = isPrefilledValueValid(fieldObject);
            }

            hiddenFields[fieldObject.name] = hide;

            // Auto-skip: if every field in the step is hidden — whether by prepop value
            // (hideIfHasValue) or by conditional logic (hideIfCondition) — skip the step.
            // hideIfCondition is checked inline here (read-only) to avoid writing to
            // hiddenFieldsIfCondition, which would trigger its watcher and clear prepop values.
            const stepFields = deps.activeStep.value.fields ?? [];
            const currentStepFieldsCount = stepFields.length;
            const totalHiddenCount = stepFields.filter(f => {
                if (hiddenFields[f.name] === true) {
                    return true;
                }
                return !!f.hideIfCondition && typeof f.hideIfCondition === 'function'
                    && f.hideIfCondition({ ...app }) === true;
            }).length;

            if (currentStepFieldsCount === totalHiddenCount) {
                deps.shouldAutoSkipStep.value = true;
            }
        } else {
            if (!!fieldObject.hideIfCondition && typeof fieldObject.hideIfCondition === 'function' && fieldObject.hideIfCondition({ ...app }) === true) {
                hide = true;
            }

            hiddenFieldsIfCondition[fieldObject.name] = hide;
        }

        if ((hiddenFields[fieldObject.name] === true) ||
            (hiddenFieldsIfCondition[fieldObject.name] === true)) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Validates every visible field on the step and returns whether it may advance.
     *
     * `reveal` is true for a real Next: each field then shows its first failing message and is
     * marked `revealed` (from here on, "required" may show on blur too). Skip, radio auto-advance
     * and auto-skipped steps still run this but pass reveal: false, so nothing new appears on
     * screen; errors that are now resolved are still cleared.
     */
    async function validateForm({ reveal = true } = {}) {
        let step = deps.activeStep.value;
        let appSettings = deps.applicationSettings;
        if (!appSettings.shouldValidateForm ||
            (
                !!step.isStepReadOnly &&
                step.hasOwnProperty('isStepEditable') &&
                step.isStepEditable === false
            )
        ) {
            return true;
        }

        let validator = deps.validator.value;
        let hiddenFields = deps.hiddenFields.value;
        let hiddenFieldsIfCondition = deps.hiddenFieldsIfCondition.value;
        let app = deps.app.value;
        let config = deps.config.value;
        let allFields = Object.keys(validator);

        for (const field of allFields) {
            const entry = validator[field];
            if ((hiddenFields[field] === true) ||
                (hiddenFieldsIfCondition[field] === true)) {
                entry.isValid = true;
                entry.errorMessage = [];
                continue;
            }

            const isAddress = field === "address";
            const result = isAddress ?
                addressFieldValidationHelper(entry.value, config.country) :
                fieldValidationHelper(entry, app, config.country);
            entry.isValid = result.isValid;

            if (isAddress) {
                // Set here as well as on every address edit: initStep() rebuilds the validator,
                // and an address nobody touched since would otherwise reveal no part errors.
                entry.invalidAddressFields = result.invalidFields ?? [];
            }

            if (reveal) {
                entry.errorMessage = visibleErrors({ trigger: TRIGGERS.SUBMIT, value: entry.value, result });
                entry.revealed = true;
                // The address component watches this to show the errors of its parts.
                entry.revealCount = (entry.revealCount ?? 0) + 1;
            } else if (result.isValid) {
                entry.errorMessage = [];
            }

            if (!!result.isValid && !isAddress) {
                const emailFieldsToValidate = ['email'];
                if (emailFieldsToValidate.includes(field)) {
                    await validateEmailWithLC(entry, { reveal });
                }
            }
        }

        return isValidForm();
    }

    // Each initStep() run gets a number. It is fired without being awaited and waits out
    // the step's loading animation, so the applicant can be on another step by the time it
    // resumes; a run that is no longer the latest must stop rather than apply its step's
    // hidden fields, validator or auto-skip to whatever step is showing now.
    let initStepRun = 0;

    async function initStep() {
        const run = ++initStepRun;
        const isStale = () => run !== initStepRun;
        let step = deps.activeStep.value;

        // lets skip activeStep if condition is met
        if (deps.devForceHidden?.value) {
            // persistent force-hidden mode: never auto-skip
        } else if (deps.devStepOverride?.value) {
            deps.devStepOverride.value = false;
        } else if (step && !!step.hideIfCondition && typeof step.hideIfCondition === 'function' && step.hideIfCondition({ ...deps.app.value }) === true) {
            await deps.loading.resolveLoadingAnimation();
            if (isStale()) {
                return;
            }

            deps.shouldAutoSkipStep.value = true;
            return;
        }

        await deps.loading.resolveLoadingAnimation();
        if (isStale()) {
            return;
        }

        // Read the application state AFTER the await, never before it. initStep() is
        // fired without being awaited (LcForm.vue watch(activeStep)), so while we are
        // suspended here the step re-renders, its fields mount and prepopulate, and
        // updateApplication() may replace app.value with a brand new object
        // (useApplicationCore.js). A reference captured before the await would then be
        // orphaned, and every field below would be seeded from that dead object —
        // e.g. an address restored from localStorage came back as null and blocked
        // Next with "Address is required" on a visibly complete address.
        // hideField() already reads deps.app.value on each call; this keeps us consistent.
        const app = deps.app.value;

        deps.editActiveStep.value = false;

        deps.hiddenFields.value = {};
        deps.hiddenFieldsIfCondition.value = {};

        // validator helper
        let newValidator = {};
        const currentFields = step ? step.fields : null;
        if (currentFields) {
            for (const field of currentFields) {
                newValidator[field.name] = {
                    value: app[field.name] ?? null,
                    // the person changed this field on this step (validation-display.js)
                    isDirty: false,
                    // Next has been pressed on this step; revealCount goes up on each press
                    revealed: false,
                    revealCount: 0,
                    isValid: false,
                    rules: field.validationRules ?? "",
                    // read by the opt-in `inChoices` rule (value must be one of the options shown)
                    choices: field.choices ?? null,
                    // what is on screen (zero or one message); isValid is the truth
                    errorMessage: [],
                    invalidAddressFields: [],
                };

                // we need to set the validator before calling hideField so it's accessible
                deps.validator.value = newValidator;

                let onInitStep = true;
                hideField(field, onInitStep);
            }
        }

        deps.validator.value = newValidator;
    }

    async function validateEmailWithLC(validatorElement, { reveal = true } = {}) {
        const result = await validateEmailWithBackend(validatorElement.value);
        if (result.isValid) {
            validatorElement.isValid = true;
            validatorElement.errorMessage = [];
        } else {
            validatorElement.isValid = false;
            if (reveal) {
                validatorElement.errorMessage = [EMAIL_UNDELIVERABLE_MESSAGE];
            }
        }
    }

    function isValidForm() {
        let validator = deps.validator.value;
        let hiddenFields = deps.hiddenFields.value;
        let hiddenFieldsIfCondition = deps.hiddenFieldsIfCondition.value;
        // Hidden fields are treated as valid. Today the only caller is validateForm(),
        // which already mutates validator[field].isValid=true for hidden fields before
        // calling us — so the check is a no-op in that path. It's kept so this function
        // stays correct standalone: if a reactive consumer is ever added (e.g. a
        // `computed(() => isValidForm())` that gates a Next button on regular steps),
        // it won't report a false negative while validator state is still at its
        // initStep defaults.
        let fieldsWithError = Object.entries(validator).filter(([key, validatorField]) => {
            if (hiddenFields[key] === true || hiddenFieldsIfCondition[key] === true) {
                return false;
            }
            return !validatorField.isValid;
        });
        return fieldsWithError.length === 0;
    }

    function handleHiddenFieldsIfConditionChange(hiddenFields) {
        let validator = deps.validator.value;
        let nullableFields = {};
        Object.keys(hiddenFields).forEach(key => {
            if (hiddenFields[key] === true) {
                nullableFields[key] = null;
                if (validator.hasOwnProperty(key)) {
                    validator[key].isValid = false;
                    validator[key].value = null;
                    validator[key].errorMessage = [];
                }
            }
        });
        if (Object.keys(nullableFields).length) {
            deps.updateApplication(nullableFields);
        }
    }

    return {
        isHiddenField,
        hideField,
        isPrefilledValueValid,
        validateForm,
        initStep,
        validateEmailWithLC,
        isValidForm,
        handleHiddenFieldsIfConditionChange,
    };
}
